<template>
  <div
    v-if="!!props.job && formattedJob.isVisible"
    class="i-career-details-card"
  >
    <div
      class="top"
      @click="$emit('jobPageRedirect', job.id)"
    >
      <div class="info">
        <div class="title">{{ formattedJob.title }}</div>
        <div
          class="badge"
          :style="{ backgroundColor: formattedJob.color }"
        >
          {{ formattedJob.match }}
        </div>
      </div>
      <div class="salaries">
        <div class="salary">{{ formattedJob.salary.entry }} <span class="title">Entry Level</span></div>
        <div class="salary">{{ formattedJob.salary.mid }} <span class="title">Mid Level</span></div>
        <div class="salary">{{ formattedJob.salary.senior }} <span class="title">Senior Level</span></div>
      </div>
    </div>
    <div
      class="mid"
      @click="$emit('jobPageRedirect', job.id)"
    >
      <div class="overview">
        {{ formattedJob.briefDescription[0] }} {{ isNotMobile ? formattedJob.briefDescription[1] : "" }}
      </div>
      <iBaseTextCta
        text="Learn More"
        @cta-clicked="$emit('jobPageRedirect', job.id)"
      />
    </div>
    <div
      v-if="showJobFeedback"
      class="bottom"
    >
      <div class="feedbacks">
        <iFavoriteJob
          class="action"
          :job-id="formattedJob.id"
          :is-favorite="formattedJob.isFavorite"
          :job-feedback="formattedJob.feedback"
          @after-like-action="scrollToTop"
        />
        <iRemoveJob
          class="action"
          :job-id="formattedJob.id"
          :job-feedback="formattedJob.feedback"
          :job-title="formattedJob.title"
        />
        <iNewCareers class="action" />
      </div>
      <iOnboardingTooltip
        v-if="props.isFirst"
        :value="4"
      />
    </div>
    <div
      v-else
      class="fake-bottom"
      @click="$emit('jobPageRedirect', job.id)"
    ></div>
  </div>
</template>

<script setup lang="ts">
import { computed, type PropType } from "vue"
import { useUserStore } from "@/stores/user"
import { breakpointSm } from "@/styles/variables"

import scrollToTop from "@/composables/scrollToTop"
import useIsScreenSize from "@/composables/useIsScreenSize"
import { numberAsCurrency } from "@/composables/numberAsCurrency"

import iBaseTextCta from "@/components/base/BaseTextCta.vue"
import iFavoriteJob from "@/components/report/careers/FavoriteJob.vue"
import iRemoveJob from "@/components/report/careers/RemoveJob.vue"
import iNewCareers from "@/components/report/careers/NewCareers.vue"
import iOnboardingTooltip from "@/components/report/OnboardingTooltip.vue"

//TYPES
import type { ExtendedRecommendedJob } from "@/types"

//EMITS
const emit = defineEmits(["jobPageRedirect"])

// COMPOSABLES AND STORES
const isNotMobile = useIsScreenSize(breakpointSm)
const userStore = useUserStore()

// PROPS
const props = defineProps({
  job: {
    type: Object as PropType<ExtendedRecommendedJob>,
    required: true
  },
  isFirst: {
    type: Boolean,
    required: true
  }
})

// COMPUTED
const showJobFeedback = computed(() => {
  return (
    !!formattedJob.value &&
    !formattedJob.value?.isCurrentJob &&
    !formattedJob.value?.isActivelyExploringJob &&
    !formattedJob.value?.isSampleJob &&
    !userStore.isTierFree
  )
})

const formattedJob = computed(() => {
  const job = props.job
  return {
    ...job,
    briefDescription: job.overview?.replace(/([.?!])\s*(?=[A-Z])/g, "$1|").split("|"),
    match: job.matchScore ? `You are a ${job?.badge} match` : `${job?.badge} Career`,
    salary: {
      entry: job.salaryPositions?.entry ? numberAsCurrency(job.salaryPositions?.entry) : "N/A",
      mid: job.salaryPositions?.mid ? numberAsCurrency(job.salaryPositions?.mid) : "N/A",
      senior: job.salaryPositions?.senior ? numberAsCurrency(job.salaryPositions?.senior) : "N/A"
    }
  }
})
</script>

<style lang="scss" scoped>
.i-career-details-card {
  padding: 20px;
  color: $pickle-bluewood800;

  @include border-radius-lg;

  border: 1px solid $zumthor100;

  @include grid($cols: 1fr, $rows: auto 1fr auto, $align-items: stretch);

  &:hover {
    border: 1px solid $azure-radiance200;
  }

  .top {
    @include flex(column, start, stretch, $gap: 20px);

    padding-bottom: 20px;
    cursor: pointer;

    .info {
      @include flex(column, start, start, $gap: 5px);

      .title {
        font-size: 24px;
        line-height: 31px;
        font-weight: 700;
        color: picklebluewood900;
      }

      .badge {
        @include border-radius-lg;

        white-space: nowrap;
        color: $white;
        border-width: 1px;
        border-style: solid;
        padding: 2px 10px;

        @include text-sm;
      }
    }

    .salaries {
      @include flex(row, space-between, center, wrap, $gap: 5px);

      @media screen and (min-width: $breakpoint-sm) {
        justify-content: start;
        gap: 50px;
      }

      @media screen and (min-width: $breakpoint-lg) {
        justify-content: space-between;
        gap: 5px;
      }

      .salary {
        @include flex(column, start, center, $gap: 2px);

        font-weight: 500;

        @include text-md;

        @media screen and (min-width: $breakpoint-sm) {
          @include text-lg;
        }

        .title {
          @include text-xs;

          color: $comet500;

          @media screen and (min-width: $breakpoint-sm) {
            @include text-sm;
          }
        }
      }
    }
  }

  .mid {
    @include flex(column, space-between, start, $gap: 15px);

    border-top: 1px solid $zumthor100;
    padding: 20px 0;
    cursor: pointer;

    .overview {
      font-weight: 500;

      @include text-md;
    }
  }

  .bottom {
    border-top: 1px solid $zumthor100;
    position: relative;

    .feedbacks {
      @include flex(row, $justify: center, $align: center, $gap: 0);
      @include border-radius-md;

      border: 1px solid $zumthor100;
      width: 100%;
      margin-top: 20px;
      overflow: hidden;
    }
  }

  .fake-bottom {
    cursor: pointer;

    @media screen and (min-width: $breakpoint-lg) {
      min-height: 83px;
    }
  }
}
</style>

<style lang="scss">
.i-career-details-card {
  .pad-wrap {
    padding: 0;
    width: 100%;
    cursor: pointer;

    @media screen and (min-width: $breakpoint-sm) {
      padding: 15px 0;
    }

    &:hover {
      background-color: $azure-radiance50;
    }
  }

  .pad-wrap > a {
    width: 100%;
    padding: 8px 5px;

    @media screen and (min-width: $breakpoint-sm) {
      padding: 5px;
    }
  }

  .action:not(:nth-last-child(1 of .action)).pad-wrap > a,
  .action:not(:nth-last-child(1 of .action)) .pad-wrap > a {
    border-right: 1px solid $zumthor100;
  }
}
</style>
